import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import SimplePlayer from "./ActividadesUtils/SimplePlayer";
import {v4 as uuid} from "uuid"
import { lightButtonStyles, shuffle } from "./ActividadesUtils/ActividadesUtils";
import { useContext } from "react";
import { ActividadContext, PDFContext } from "./ActividadesUtils/ActWrapper";
import { colors } from "./ActividadesUtils/ActividadesUtils";
import Lightbox from "./ActividadesUtils/Lightbox/Lightbox";
import LightboxWrapper from "./ActividadesUtils/Lightbox/LightboxWrapper";
import { Trans } from "react-i18next";
import ImagenValidada from "./ActividadesUtils/RecursosValidados/ImagenValidada";

// Un arrastrable que cambia de columna se desmonta y se vuelve a montar en el
// Droppable destino, así que nace ya con su ancho final y CSS no tiene desde dónde
// interpolar. Este hook lo hace nacer compacto y lo ensancha de inmediato.
//
// El useLayoutEffect corre ANTES de que el navegador pinte: se lee offsetWidth para
// forzar un reflow que fije el ancho compacto como valor de partida, y en el mismo
// tick se marca como montado. Así la transición arranca sin esperar un frame.
// (Con requestAnimationFrame se perdía ~1 frame y el arranque se notaba tardío.)
function useAnchoAlMontar(ref) {
  const [montado, setMontado] = useState(false);
  useLayoutEffect(() => {
    if (ref.current) {
      // Lectura intencional: fuerza el reflow con el ancho compacto ya aplicado.
      // eslint-disable-next-line no-unused-expressions
      ref.current.offsetWidth;
    }
    setMontado(true);
  }, [ref]);
  return montado;
}

// Cuerpo del arrastrable. Es un componente propio (y no el contenido directo del
// render-prop del Draggable) para poder usar el hook de ancho: los hooks no pueden
// llamarse dentro de una función-render.
function ItemArrastrable({ provided, snapshot, item, enBandeja, enRejilla, setLightboxValue }) {
  const nodoRef = useRef(null);
  const montado = useAnchoAlMontar(nodoRef);
  // Compacto en la bandeja, mientras se arrastra (aterrizaje incluido) y durante el
  // primer frame tras montarse en un contenedor nuevo, que es de donde arranca la
  // transición hacia w-full.
  //
  // OJO: el ancho NO puede animarse durante isDropAnimating. En ese tramo rbd tiene el
  // elemento en position:fixed, así que w-full se mide contra el viewport y la caja se
  // estira hasta el borde derecho de la pantalla. Hay que esperar a que aterrice.
  const compacto = enBandeja || snapshot.isDragging || !montado;
  const rbdStyle = provided.draggableProps.style;
  // Dentro del contenedor, los items de imagen van en rejilla de 2 columnas: si
  // ocuparan el ancho completo, el aspect-square los volvería cuadrados enormes.
  const anchoClase = compacto ? 'w-[110px] md:w-[150px]' : 'w-full';
  // En rejilla la separación la da el gap del contenedor, no los márgenes del item:
  // dejarlos sumaría al 100 % del ancho de columna y volvería a desbordar.
  const margenClase = enRejilla && !compacto ? '' : `mb-2 my-1 ${enBandeja ? 'mx-1' : ''}`;

  return (
    <div
      ref={(el) => {
        provided.innerRef(el);
        nodoRef.current = el;
      }}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      className={`${enRejilla && !compacto ? 'p-1' : 'px-2 py-2 md:px-4 md:py-2'} text-white text-sm md:text-base relative rounded-xl overflow-hidden flex ${margenClase} ${anchoClase} ${item.imagen ? 'aspect-square' : 'min-h-[48px] md:min-h-[56px] h-auto items-center'}`}
      style={{
        userSelect: "none",
        backgroundColor: item.background,
        ...rbdStyle,
        // rbd fija un width en px al levantar el elemento y se esparce después del
        // className, así que hay que neutralizarlo para que mande la clase.
        ...(compacto ? { width: undefined } : {}),
        // rbd fija también un height en px, con el alto que el elemento tenía en su
        // origen. En los items de imagen eso gana al aspect-square y salen rectangulares
        // al arrastrarlos desde un contenedor: hay que liberarlo para que el cuadrado
        // se calcule desde el ancho compacto.
        ...(compacto && item.imagen ? { height: undefined } : {}),
        // Acorta el vuelo de vuelta de rbd (por defecto ~330 ms), que es el tramo previo
        // al ensanchamiento. Es lo único seguro que se puede tocar aquí: el width debe
        // quedarse como lo fija rbd mientras el elemento está en position:fixed.
        ...(snapshot.isDropAnimating && rbdStyle?.transition
          ? { transitionDuration: '120ms' }
          : {}),
        // Suaviza el cambio de ancho. Durante el arrastre no se toca: rbd sigue al
        // puntero con su propio transform y encimarle una transición de width lo descuadra.
        transition: snapshot.isDragging
          ? rbdStyle?.transition
          : [rbdStyle?.transition, 'width 160ms ease-out'].filter(Boolean).join(', '),
      }}
    >
      {/* form items from backend */}
      <div className="flex items-center justify-center w-full" style={{backgroundColor:item.background}}>
        {item.imagen ?
        (
          <LightboxWrapper handleClick={()=>setLightboxValue(item.imagen)} wrapperStyles={'w-full h-full !absolute top-0 left-0'}>
            <ImagenValidada
              src={item.imagen}
              className="z-0 bg-white absolute w-full h-full top-0 right-0 object-fill block border brightness-75 square-image"
            />
          </LightboxWrapper>
        )
        : ("")}
        {item.audio ? (<SimplePlayer audio={item.audio} size={'text-xl !p-3 mr-2'}/>) :('')}
        {item.texto ? <span className="text-sm z-30 text-center break-words w-full">{item.texto}</span>: ''}
      </div>
    </div>
  );
}

function ArrastrarAContenedores({contenedores, contenedorItems, evaluable, umaBloque, guardarCalificacion, instrucciones}) {
  const [iniciado, setIniciado] = useState(false)
  const contenedorItemsArr = [...contenedorItems].map((item,index)=>({...item,  background:colors[index]}))
  const columnsFromBackend = [{ texto: "", items: shuffle(contenedorItemsArr) , _id: uuid(), },...contenedores]
  const [columns, setColumns] = useState(columnsFromBackend);
  const [lightboxValue, setLightboxValue] = useState('')
  const activityContext = useContext(ActividadContext)
  const pdfContext = useContext(PDFContext);
  const isGeneratingPDF = pdfContext?.isGeneratingPDF || false;

  const onDragEnd = (result, columns, setColumns) => {
    setIniciado(true)
    if (!result.destination) return;
    const { source, destination } = result;
  
    if (source.droppableId !== destination.droppableId) {
      const sourceColumn = columns[source.droppableId];
      const destColumn = columns[destination.droppableId];
      const sourceItems = [...sourceColumn.items];
      const destItems = [...destColumn.items];
      const [removed] = sourceItems.splice(source.index, 1);
      destItems.splice(destination.index, 0, removed);
      setColumns({
        ...columns,
        [source.droppableId]: {
          ...sourceColumn,
          items: sourceItems
        },
        [destination.droppableId]: {
          ...destColumn,
          items: destItems
        }
      });
    } else {
      const column = columns[source.droppableId];
      const copiedItems = [...column.items];
      const [removed] = copiedItems.splice(source.index, 1);
      copiedItems.splice(destination.index, 0, removed);
      setColumns({
        ...columns,
        [source.droppableId]: {
          ...column,
          items: copiedItems
        }
      });
    }
  };

  const currentArr = Object.keys(columns).map((key) => columns[key]);
  const respuestas = currentArr
    .map((item) => {
      return item.items.map((el) => {
        return el.parentId === item._id;
      });
    })
    .flat();
  const respuestasCorrectas = respuestas.filter(Boolean).length
  const calificacion = (respuestasCorrectas / contenedorItems.length) * 100

  const calificar = () =>{
    if(iniciado){
      activityContext.postCalificacionActividad(calificacion)
      guardarCalificacion(umaBloque, respuestasCorrectas, JSON.stringify(columns))
    }
    else if(!iniciado)activityContext.postCalificacionActividad(null)
  }

  const handleCalificar = () => {
    if(activityContext !== undefined){
      calificar()
    }
  };

  const autocompletarActividad = () =>{
    const respuestasCorrectas = columnsFromBackend.map((item)=>{
      const respuestas  = contenedorItemsArr.filter((el)=>(el.parentId === item._id))
      return {...item, ...{items:respuestas}}
    })
    setColumns(respuestasCorrectas)
  }

  useEffect(()=>{
    if(!evaluable){
      autocompletarActividad()
    }else if(evaluable){
      setColumns(columnsFromBackend)
    }
  },[evaluable])
  
  const handleLimpiar = () =>{
    setColumns(columnsFromBackend);
    setIniciado(false)
  }

  return (
    <>
      {instrucciones && <div className="act-instructions mt-4 max-w-activity">{instrucciones}</div>}
      <Lightbox lightboxValue={lightboxValue} closeLightbox={()=>setLightboxValue('')}/>
      <div className={`w-full max-w-activity py-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 justify-center items-start h-full ${evaluable ? '' : 'pointer-events-none'}`}>
        <DragDropContext
          onDragEnd={(result) => onDragEnd(result, columns, setColumns)}
        >
          {Object.entries(columns).map(([columnId, column], index) => {
            return (
              <div
                className={`flex flex-col items-center p-2 min-w-0 ${index === 0 ? 'outside-grid mb-5 w-full' : 'justify-start self-start'}`}
                key={columnId}
              >
                {/* para contenedores */}
                {column.imagen ? 
                ( <>
                  <LightboxWrapper handleClick={()=>setLightboxValue(column.imagen)}>
                    <ImagenValidada 
                      src={column.imagen} 
                      className="w-32 h-32 rounded-md block shadow-lg border square-image"
                    />
                  </LightboxWrapper>
                  </>)
                : ("")}
                {column.audio ? (<SimplePlayer audio={column.audio} size={'text-xl !p-3 mr-2'}/>) :('')}
                {column.texto ? <span className="mt-1 text-center text-[#697d99] w-[150px] lg:w-[200px] text-sm md:text-base`">{column.texto}</span>: ''}
                <div className="m-2 w-full min-w-0">
                  <Droppable
                    droppableId={columnId}
                    key={columnId}
                    direction={'vertical'}
                  >
                    {(provided, snapshot) => {
                      return (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className={`p-1 md:p-2 border rounded-xl ${index === 0 ? 'grid grid-flow-col grid-rows-2 auto-cols-max justify-start overflow-x-auto h-fit w-full min-h-[70px] ' : `overflow-y-auto overflow-x-hidden h-auto w-[150px] md:w-[200px] min-h-[168px] lg:min-h-[200px] mx-auto ${column.items.some((it) => it.imagen) ? 'grid grid-cols-2 gap-1 content-start' : ''}`}`}
                          style={{background: snapshot.isDraggingOver ? "#e6e9ee" : "#ebecf0",}}
                        >
                          {column.items.map((item, itemIndex) => {
                            return (
                              <Draggable
                                key={item._id}
                                draggableId={item._id}
                                index={itemIndex}
                              >
                                {(provided, snapshot) => (
                                  <ItemArrastrable
                                    provided={provided}
                                    snapshot={snapshot}
                                    item={item}
                                    enBandeja={index === 0}
                                    enRejilla={column.items.some((it) => it.imagen)}
                                    setLightboxValue={setLightboxValue}
                                  />
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      );
                    }}
                  </Droppable>
                </div>
              </div>
            );
          })}
        </DragDropContext>
      {evaluable && !isGeneratingPDF ? 
      <div className="flex justify-center align-center w-full outside-grid mx-auto transform scale-75 md:scale-100 mt-8">
        <button className={lightButtonStyles} onClick={handleLimpiar}>
          <Trans>Limpiar</Trans>
        </button>
        <button 
            onClick={handleCalificar}
            className='border bg-nimbus text-white m-5 py-2 md:py-3 px-5 md:px-10 rounded-full inline text-sm md:text-basee'
        >
          <Trans>{iniciado ? 'Enviar y cerrar' : 'Cerrar'}</Trans>
        </button>
      </div>
      :''}
      </div>
    </>
  );
}

ArrastrarAContenedores.defaultProps = {
  contenedorItems:[],
  contenedores:[],
  evaluable:true,
}

export default ArrastrarAContenedores;
